import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTranslation } from "react-i18next";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const snapLink = keyframes`
  0%, 18% { opacity: 1; transform: scale(1) rotate(0deg); }
  42% { opacity: 0.85; transform: scale(1.06) rotate(-6deg); }
  68% { opacity: 0.35; transform: scale(0.9) rotate(10deg) translateX(6px); }
  100% { opacity: 0.12; transform: scale(0.78) rotate(14deg) translateX(14px); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function UnlinkPreviewStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "18%",
          top: 16,
          width: 48,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Inventory2OutlinedIcon sx={{ fontSize: 24, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "46%",
          top: 28,
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.12),
          border: "1.5px dashed",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          animation: `${snapLink} 2.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <LinkOutlinedIcon sx={{ fontSize: 20, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "16%",
          top: 18,
          width: 52,
          height: 52,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.14),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.2)}`,
          animation: `${bob} 2s ease-in-out infinite`,
          animationDelay: "0.2s",
          ...motionSafe,
        }}
      >
        <LinkOffOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
    </Box>
  );
}

/** Confirma desvincular media ↔ muestra, con motion. */
export default function MeetingUnlinkMuestraDialog({
  open,
  onClose,
  onConfirm,
  busy = false,
  count = 1,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.warning.main || "#ed6c02";
  const n = Math.max(1, Number(count) || 1);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      className="meeting-no-print"
      sx={{ zIndex: (th) => th.zIndex.modal + 20 }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.warning.main, 0.14)} 0%, ${alpha(
              th.palette.primary.main,
              0.05,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <LinkOffOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.unlinkMuestraTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {t("Meetings.unlinkMuestraHint")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            disabled={busy}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <UnlinkPreviewStage accent={accent} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            fontWeight: 600,
            animation: `${rise} 300ms ease both`,
            ...motionSafe,
          }}
        >
          {t("Meetings.unlinkMuestraBody", { count: n })}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontWeight: 700, minHeight: 44 }}
        >
          {t("Meetings.cancel")}
        </Button>
        <Button
          color="warning"
          variant="contained"
          disabled={busy}
          onClick={onConfirm}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LinkOffOutlinedIcon />
            )
          }
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
          }}
        >
          {t("Meetings.unlinkMuestraConfirm")}
        </Button>
      </Stack>
    </Dialog>
  );
}
